import { Search, FolderOpen, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useProjects, type Project } from "@/pages/MCPs/hooks/useProjects";
import { usePlaygroundProject } from "@/atoms/playground-project";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const projectLabel = (project: Project): string =>
  project.name ?? project.displayName ?? project.id;

export const PlaygroundProjectSelect = ({
  disabled,
}: {
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    data: projects = [],
    isLoading,
    isFetching,
    isStale,
    isSuccess,
    refetch,
  } = useProjects();
  const { projectId, setProjectId } = usePlaygroundProject();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedProject = projects.find((project) => project.id === projectId);

  const filteredProjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((project) =>
      projectLabel(project).toLowerCase().includes(query),
    );
  }, [projects, search]);

  // The selection is persisted in localStorage, so it outlives an org switch.
  // Once the current org's projects are in, drop a selection that isn't among
  // them rather than scoping requests to a project this org can't see. Gated
  // on isSuccess so a failed fetch (empty data) doesn't clear it.
  useEffect(() => {
    if (!projectId || !isSuccess || isFetching) return;
    if (projects.some((project) => project.id === projectId)) return;

    setProjectId("");
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
  }, [projectId, isSuccess, isFetching, projects, setProjectId, queryClient]);

  const handleChange = (value: string) => {
    setProjectId(value);
    // Conversations are scoped by the project header, so refetch the list.
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    // Pick up projects created elsewhere without needing a page refresh, but
    // serve the cache while it's fresh — refetch() ignores staleTime, so an
    // unconditional call here would hit the network on every open.
    if (nextOpen && isStale) {
      refetch();
    }
    setOpen(nextOpen);
    if (!nextOpen) setSearch("");
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild disabled={isLoading || disabled}>
        <Button
          type="button"
          aria-busy={isFetching}
          className="h-9 w-full justify-between gap-2 rounded-[8px] border border-border bg-input p-3 text-sm font-normal text-foreground hover:bg-input"
        >
          {isLoading ? (
            // The persisted project's label isn't known until the list arrives,
            // so stand in for the trigger label rather than rendering an empty one.
            <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
              <FolderOpen className="size-4 shrink-0" />
              <Spinner className="size-3.5" />
              {t("playground.loadingProjects")}
            </span>
          ) : (
            <span className="flex min-w-0 items-center gap-2">
              <FolderOpen className="size-4 shrink-0 text-foreground" />
              <span className="min-w-0 truncate">
                {selectedProject
                  ? projectLabel(selectedProject)
                  : t("playground.selectProject")}
              </span>
            </span>
          )}
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[320px] w-[var(--radix-dropdown-menu-trigger-width)] max-w-sm overflow-y-auto border-border bg-card p-1"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div
          className="relative mb-1"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Search className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("playground.searchProjectPlaceholder")}
            aria-label={t("playground.searchProjectPlaceholder")}
            className="h-8 w-full rounded-md border border-border bg-transparent ps-7 pe-2 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:border-primary [&::-webkit-search-cancel-button]:opacity-50 [&::-webkit-search-cancel-button]:grayscale"
          />
        </div>
        {filteredProjects.map((project) => (
          <DropdownMenuItem
            key={project.id}
            onSelect={() => handleChange(project.id)}
            className="flex min-w-0 items-center justify-between gap-2 text-sm"
          >
            <span className="min-w-0 truncate">{projectLabel(project)}</span>
          </DropdownMenuItem>
        ))}
        {!filteredProjects.length && (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">
            {t("playground.noProjectsFound")}
          </p>
        )}
        {isFetching && !isLoading ? (
          // Refetch triggered by opening the menu: keep the known projects
          // usable and show the refresh happening underneath them.
          <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
            <Spinner className="size-3.5" />
            {t("playground.loadingProjects")}
          </div>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
